import styled from '@emotion/styled';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { BoxProps } from '../box/Box';
import { getBoxStyles } from '../box/Box';
import { themeVars } from '../theme/theme';

export type SegmentedControlItem<VALUE extends string | number = string | number> = {
  value: VALUE;
  label: React.ReactNode;
};

export type SegmentedControlProps<VALUE extends string | number = string | number> = BoxProps & {
  value?: VALUE;
  data: (VALUE | SegmentedControlItem<VALUE>)[];
  onChange?: (value: VALUE) => void;
  className?: string;
};

const Component = <VALUE extends string | number = string | number>({
  data,
  value,
  onChange,
  className,
  ...props
}: SegmentedControlProps<VALUE>) => {
  const [refs, setRefs] = useState({} as Record<VALUE, HTMLElement | null>);
  const [selectedRef, setSelectedRef] = useState<HTMLElement | null>(null);
  const [position, setPosition] = useState({ left: 0, width: 0 });

  const resizeObserver = useRef<ResizeObserver>();
  const transitionTimeout = useRef<number>(-1);
  const indicatorRef = useRef<HTMLDivElement>(null);

  const setElementRef = (element: HTMLElement | null, val: VALUE) => {
    refs[val] = element;
    setRefs(refs);
  };

  const _data = data.map<SegmentedControlItem<VALUE>>((item) =>
    typeof item === 'string' || typeof item === 'number' ? { label: item, value: item } : item,
  );

  const updatePosition = useCallback(() => {
    if (!selectedRef) return;
    setPosition({ left: selectedRef.offsetLeft, width: selectedRef.offsetWidth });
  }, [selectedRef]);

  const updatePositionWithoutAnimation = useCallback(() => {
    window.clearTimeout(transitionTimeout.current);
    if (indicatorRef.current) {
      indicatorRef.current.style.transitionDuration = '0ms';
    }
    updatePosition();
    transitionTimeout.current = window.setTimeout(() => {
      if (indicatorRef.current) {
        indicatorRef.current.style.transitionDuration = '';
      }
    }, 30);
  }, [updatePosition]);

  useEffect(() => {
    if (value && refs[value]) setSelectedRef(refs[value]);
  }, [value, refs]);

  useEffect(() => {
    updatePosition();

    if (selectedRef) {
      resizeObserver.current = new ResizeObserver(updatePositionWithoutAnimation);
      resizeObserver.current.observe(selectedRef);

      return () => {
        resizeObserver.current?.disconnect();
      };
    }
  }, [selectedRef, updatePosition, updatePositionWithoutAnimation]);

  return (
    <Root className={className} {...props}>
      {selectedRef && (
        <Indicator
          ref={indicatorRef}
          style={{
            width: position.width,
            transform: `translateX(${position.left}px)`,
          }}
        />
      )}
      {_data.map((item, index) => {
        return (
          <Item
            key={index}
            onClick={() => onChange?.(item.value)}
            ref={(node) => setElementRef(node, item.value)}
            data-active={item.value === value}
          >
            <Label>{item.label}</Label>
          </Item>
        );
      })}
    </Root>
  );
};

const Item = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  cursor: pointer;
  border-radius: 99px;
  transition: all 0.2s ease;
  user-select: none;
  position: relative;
  flex: 1;
  color: ${themeVars.colors.coldGrey800};

  &[data-active='true'] {
    color: #000;
    font-weight: 500;
  }

  &[data-active='false'] {
    &:hover {
      background-color: ${themeVars.colors.titan300};
    }

    &:active {
      background-color: ${themeVars.colors.titan400};
    }
  }
`;

const Root = styled.div`
  ${getBoxStyles};
  min-width: 0;
  display: flex;
  padding: 2px;
  border-radius: 99px;
  transition: all 0.2s ease;
  background-color: ${themeVars.colors.titan200};
  position: relative;
`;

const Indicator = styled.div`
  position: absolute;
  top: 2px;
  left: 0px;
  bottom: 2px;
  box-shadow: 0px 2px 4px 0px #00000014;
  background-color: #fff;
  border-radius: 99px;
  transition: all 0.3s ease;
  transform: translateX(0);
  pointer-events: none;
`;

const Label = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${themeVars.text.familyBody};
  font-size: ${themeVars.text.sizeBodyXS};
  line-height: ${themeVars.text.lineHeightBodyXS};
`;

export const SegmentedControl = Object.assign(Component, {
  Item,
  Root,
  Indicator,
  Label,
});
